import { useNavigate } from "react-router-dom";
import { CalendarDays, FileText, PlayCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROSSO = "#C8102E";
const BLU = "#14284B";
const VERDE = "#15803D";

/** Auto e divano: credito al consumo auto e beni per la casa. */
function IconaLungo() {
  return (
    <svg viewBox="0 0 120 54" className="h-20 w-auto" fill="none" aria-hidden="true">
      <g strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 36h3.5l3.6-11.4A7 7 0 0 1 19.8 20h19.4a7 7 0 0 1 6.7 4.6L49.5 36H53"
          stroke={BLU} strokeWidth="2.6" />
        <path d="M11 36h37" stroke={BLU} strokeWidth="2.6" />
        <path d="M15.5 25.6h28l-2.6-4.2a4 4 0 0 0-3.4-1.9H21.5a4 4 0 0 0-3.4 1.9z"
          fill={ROSSO} fillOpacity="0.18" stroke={ROSSO} strokeWidth="2" />
        <path d="M29.5 20v5.6" stroke={ROSSO} strokeWidth="1.6" opacity="0.7" />
        <circle cx="19" cy="38.5" r="4.4" fill="#fff" stroke={BLU} strokeWidth="2.6" />
        <circle cx="40" cy="38.5" r="4.4" fill="#fff" stroke={BLU} strokeWidth="2.6" />
        <path d="M8 30.5h4M47 30.5h4" stroke={ROSSO} strokeWidth="2" />
      </g>
      <g strokeLinecap="round" strokeLinejoin="round">
        <path d="M72 30V23a5 5 0 0 1 5-5h26a5 5 0 0 1 5 5v7" stroke={BLU} strokeWidth="2.6" />
        <path d="M78 30v-6.5a2 2 0 0 1 2-2h20a2 2 0 0 1 2 2V30" fill={ROSSO} fillOpacity="0.18"
          stroke={ROSSO} strokeWidth="2" />
        <path d="M70 33.5a4.5 4.5 0 0 1 9 0V37h22v-3.5a4.5 4.5 0 0 1 9 0V43H70z"
          fill="#fff" stroke={BLU} strokeWidth="2.6" />
        <path d="M79 37h22" stroke={BLU} strokeWidth="2" opacity="0.4" />
        <path d="M74 43v4M106 43v4" stroke={BLU} strokeWidth="2.6" />
      </g>
    </svg>
  );
}

/** Tre monete impilate con il simbolo dell'euro. */
function IconaCorto() {
  return (
    <svg viewBox="0 0 120 54" className="h-20 w-auto" fill="none" aria-hidden="true">
      <g strokeLinecap="round" strokeLinejoin="round">
        {[40, 30, 20].map((cy, i) => (
          <g key={cy}>
            <path d={`M17 ${cy} a19 6.6 0 0 0 38 0 v-0.2 a19 6.6 0 0 0-38 0z`}
              fill={i === 2 ? ROSSO : "#fff"} fillOpacity={i === 2 ? 0.2 : 1}
              stroke={BLU} strokeWidth="2.4" />
            <path d={`M17 ${cy} v-4.6`} stroke={BLU} strokeWidth="2.4" />
            <path d={`M55 ${cy} v-4.6`} stroke={BLU} strokeWidth="2.4" />
          </g>
        ))}
        <ellipse cx="36" cy="15.4" rx="19" ry="6.6" fill="#fff" stroke={BLU} strokeWidth="2.4" />
        <path d="M31 12.4h9M31 15.9h9" stroke={ROSSO} strokeWidth="1.8" />
        <circle cx="88" cy="28" r="15" fill="#fff" stroke={BLU} strokeWidth="2.6" />
        <circle cx="88" cy="28" r="10.5" stroke={BLU} strokeWidth="1.2" opacity="0.35" />
        <path d="M93.5 21.5a8.6 8.6 0 1 0 0 13" stroke={ROSSO} strokeWidth="2.6" />
        <path d="M79.5 25.8h10M79.5 30.2h10" stroke={ROSSO} strokeWidth="2.6" />
      </g>
    </svg>
  );
}

/** Monitor con checklist e lente d'ingrandimento. */
function IconaMonitoraggio() {
  return (
    <svg viewBox="0 0 120 54" className="h-20 w-auto" fill="none" aria-hidden="true">
      <g strokeLinecap="round" strokeLinejoin="round">
        <rect x="26" y="6" width="68" height="34" rx="4" fill="#fff" stroke={BLU} strokeWidth="2.6" />
        <path d="M26 32h68" stroke={BLU} strokeWidth="2" opacity="0.35" />
        <path d="M50 46h20l-3.6-6H53.6z" fill="#fff" stroke={BLU} strokeWidth="2.6" />
        <path d="M44 48h32" stroke={BLU} strokeWidth="2.6" />
        <path d="M33 13.5l2.6 2.6 4.6-4.8M33 22l2.6 2.6 4.6-4.8" stroke={ROSSO} strokeWidth="2.2" />
        <path d="M46 12.6h22M46 18.4h16M46 24.6h20" stroke={BLU} strokeWidth="2"
          opacity="0.55" />
        <circle cx="78" cy="27" r="13" fill="#fff" stroke={VERDE} strokeWidth="2.8" />
        <circle cx="78" cy="27" r="8.4" stroke={VERDE} strokeWidth="1.4" opacity="0.4" />
        <path d="M87.6 36.6l8.4 8.4" stroke={VERDE} strokeWidth="3.4" />
      </g>
    </svg>
  );
}

/** Grafico a barre con lente: confronto di mercato. */
function IconaBenchmark() {
  return (
    <svg viewBox="0 0 120 54" className="h-20 w-auto" fill="none" aria-hidden="true">
      <g strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 46h92" stroke={BLU} strokeWidth="2.6" />
        <rect x="20" y="30" width="12" height="16" fill="#fff" stroke={BLU} strokeWidth="2.4" />
        <rect x="38" y="20" width="12" height="26" fill={ROSSO} fillOpacity="0.2"
          stroke={ROSSO} strokeWidth="2.4" />
        <rect x="56" y="26" width="12" height="20" fill="#fff" stroke={BLU} strokeWidth="2.4" />
        <rect x="74" y="12" width="12" height="34" fill={ROSSO} fillOpacity="0.2"
          stroke={ROSSO} strokeWidth="2.4" />
        <path d="M18 22l16-8 16 6 18-12 18 4" stroke={BLU} strokeWidth="2" opacity="0.35" />
        <circle cx="95" cy="20" r="12" fill="#fff" stroke={VERDE} strokeWidth="2.8" />
        <path d="M89.5 20h11M95 14.5v11" stroke={VERDE} strokeWidth="2.2" />
        <path d="M103.6 28.6l6.4 6.4" stroke={VERDE} strokeWidth="3.2" />
      </g>
    </svg>
  );
}

const CANALI = [
  { key: "lungo", titolo: "CANALE LUNGO",
    rotta: "/", accento: ROSSO, Icona: IconaLungo },
  { key: "corto", titolo: "CANALE CORTO",
    rotta: "/canale-corto", accento: BLU, Icona: IconaCorto },
];

export default function SceltaCanale({ onScegli }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const disponibili = CANALI.filter((c) => (user?.canali || ["lungo"]).includes(c.key));

  const scegli = (c) => {
    onScegli(c.key);
    navigate(c.rotta, { replace: true });
  };

  const schede = [
    ...disponibili.map((c) => ({ ...c, testid: `btn-canale-${c.key}`, azioneLabel: "Entra",
      azione: () => scegli(c) })),
    ...((user?.pagine || []).includes("report_cq")
      ? [{ key: "cq", titolo: "REPORT CQ", accento: "#0F766E", Icona: IconaMonitoraggio,
          testid: "btn-dashboard-cq", azioneLabel: "Apri", azione: () => navigate("/dashboard-cq") }]
      : []),
    ...((user?.pagine || []).includes("monitoraggio_globale")
      ? [{ key: "monitoraggio", titolo: "MONITORAGGIO", accento: VERDE, Icona: IconaMonitoraggio,
          testid: "btn-monitoraggio", azioneLabel: "Apri",
          azione: () => navigate("/monitoraggio-dbe") }]
      : []),
    ...((user?.pagine || []).includes("benchmark")
      ? [{ key: "benchmark", titolo: "MERCATO / BENCHMARK", accento: "#B45309",
          Icona: IconaBenchmark, testid: "btn-benchmark", azioneLabel: "Apri",
          azione: () => navigate("/mercato-benchmark") }]
      : []),
  ];

  return (
    <div className="min-h-screen flex flex-col" data-testid="scelta-canale"
      style={{ background: "radial-gradient(70% 55% at 12% 88%, rgba(20,40,75,0.20) 0%, rgba(20,40,75,0) 70%),"
        + " radial-gradient(55% 45% at 92% 18%, rgba(200,16,46,0.07) 0%, rgba(200,16,46,0) 72%),"
        + " radial-gradient(120% 85% at 50% 6%, #FFFFFF 0%, rgba(255,255,255,0) 58%),"
        + " linear-gradient(125deg, #BFC9D9 0%, #D5DCE8 34%, #E8ECF3 64%, #F5F7FA 100%)" }}>
      <div className="flex-1 grid place-items-center px-6 py-6">
        <div className="w-full max-w-6xl flex flex-col items-center">
          <img src="/dbe-logo.png" alt="DBE · Consulenza finanziaria" data-testid="logo-dbe"
            className="h-20 sm:h-24 lg:h-28 w-auto" />

          <div className="mt-8 flex flex-wrap justify-center gap-6 w-full max-w-[1080px]">
            {schede.map((c) => (
              <button key={c.key} type="button" onClick={c.azione}
                data-testid={c.testid}
                className="group relative overflow-hidden rounded-2xl bg-white px-6 py-7 text-left
                  w-full sm:w-[300px] lg:w-[330px] max-w-[330px]
                  ring-1 ring-black/5 shadow-[0_12px_28px_-14px_rgba(20,40,75,0.28)]
                  transition-all duration-300 hover:-translate-y-1.5
                  hover:shadow-[0_22px_50px_-16px_rgba(20,40,75,0.5)]
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#14284B]/40">
                <span className="absolute inset-x-0 top-0 h-1.5 transition-all duration-300
                  group-hover:h-2.5" style={{ background: c.accento }} />
                <span className="absolute -right-10 -bottom-12 h-36 w-36 rounded-full opacity-[0.06]
                  transition-transform duration-500 group-hover:scale-125"
                  style={{ background: c.accento }} />
                <span className="relative block transition-transform duration-300
                  group-hover:-translate-y-1">
                  <c.Icona />
                </span>
                <span className="relative mt-5 block text-xl lg:text-2xl font-black tracking-tight"
                  style={{ color: BLU }}>
                  {c.titolo}
                </span>
                <span className="relative mt-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5
                  text-sm font-semibold text-white transition-all duration-300
                  group-hover:gap-3.5" style={{ background: c.accento }}>
                  {c.azioneLabel}
                  <span aria-hidden="true">→</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="px-6 py-5 flex items-center justify-between text-xs text-slate-400">
        <span>{user?.name || user?.email}</span>
        <div className="flex items-center gap-4">
          {(user?.pagine || []).includes("procedure") && (
            <button type="button" onClick={() => navigate("/procedure")} data-testid="btn-procedure"
              title="Archivio procedure in PDF"
              className="inline-flex items-center gap-2.5 rounded-full border-2 bg-white px-5 py-2.5
                text-sm font-bold uppercase tracking-wide shadow-md
                hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
              style={{ borderColor: BLU, color: BLU }}>
              <FileText size={18} /> Procedure
            </button>
          )}
          {(user?.pagine || []).includes("inxpire") && (
            <button type="button" onClick={() => navigate("/inxpire")} data-testid="btn-inxpire"
              title="Documenti e video tutorial Inxpire"
              className="inline-flex items-center gap-2.5 rounded-full border-2 bg-white px-5 py-2.5
                text-sm font-bold uppercase tracking-wide shadow-md
                hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
              style={{ borderColor: BLU, color: BLU }}>
              <PlayCircle size={18} /> Inxpire
            </button>
          )}
          {(user?.pagine || []).includes("calendario_corto") && (
            <button type="button" onClick={() => navigate("/calendario-corto")} data-testid="btn-calendario-corto"
              title="Area operativa: presenze settimanali in filiale"
              className="inline-flex items-center gap-2.5 rounded-full border-2 bg-white px-5 py-2.5
                text-sm font-bold uppercase tracking-wide shadow-md
                hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
              style={{ borderColor: BLU, color: BLU }}>
              <CalendarDays size={18} /> Calendario corto
            </button>
          )}
          <button type="button" onClick={logout} data-testid="btn-logout-scelta"
            className="hover:text-[#C8102E] transition-colors duration-200">
            Esci
          </button>
        </div>
      </div>
    </div>
  );
}
